import { tWithKeyAttr as t } from "../../i18nHelper";
import React, { useEffect, useState } from "react";
import styles from "./groupTreatmentPlans.module.css";
import { ITreatmentGroup } from "../../services/types/utils.types";
import { TAppointmentsProgramsData } from "../../services/types/utils.types";
import {
  useGetGroupTreatmentPlansQuery,
  useDeleteAppointmentsProgramsMutation,
  useLazyGetAllMetaTablesQuery,
} from "../../services/api/utils.api";
import Loader from "../loader/loader";
import BackArrowButton from "../backArrowButton/backArrowButton";
import ActionButtonTooltip from "../ActionButtonTooltip/actionButtonTooltip";
import ListPagination from "../listPagination/listPagination";
import AddTreatmentPlan from "../addTreatmentPlan/addTreatmentPlan";
import Modal from "../modals/modal/Modal";
import DeleteConfirmation from "../modals/deleteConfirmation/deleteConfirmation";
import GeneralSuccessPopup from "../modals/generalSuccessPopup/generalSuccessPopup";
import ErrorPopup from "../modals/errorPopup/errorPopup";
import AppointmentsProgramSchedulePopup from "../modals/appointmentsProgramSchedulePopup/appointmentsProgramSchedulePopup";
import RestoreProgramButton from "../restoreProgramButton/restoreProgramButton";
import { useAppSelector } from "../../services/hooks";
import { adminSelector } from "../../services/selectors/user.selectors";
import { appointmentsProgramsDataSelector } from "../../services/selectors/utils.selectors";
import { IUserReport } from "../../services/types/users.types";
import { noDataLabel } from "../../utils/constants";
import { formatDateTime, getPrintDate } from "../../utils/utils";

interface GroupTreatmentPlansProps {
  group: ITreatmentGroup;
  onClose: () => void;
  onAddNew: () => void;
}

function GroupTreatmentPlans({ group, onClose, onAddNew }: GroupTreatmentPlansProps) {
  const admin = useAppSelector(adminSelector);
  const appointmentsProgramsData = useAppSelector(appointmentsProgramsDataSelector);
  
  const { data: treatmentPlans, isLoading, error, refetch } = useGetGroupTreatmentPlansQuery(
    { groupId: group.id },
    { skip: !group.id }
  );

  const [getAllMetaTables] = useLazyGetAllMetaTablesQuery();
  const [deleteProgram] = useDeleteAppointmentsProgramsMutation();

  const [openModal, setOpenModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [openModalSuccess, setOpenModalSuccess] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);
  const [programToEdit, setProgramToEdit] = useState<TAppointmentsProgramsData | undefined>(undefined);
  const [showAddOrEditProgramForm, setShowAddOrEditProgramForm] = useState(false);
  const [programToDelete, setProgramToDelete] = useState<TAppointmentsProgramsData | undefined>(undefined);
  const [limits, setLimits] = useState({ start: 1, end: 8 });

  useEffect(() => {
    if (admin?.companyId) {
      getAllMetaTables(admin.companyId);
    }
  }, [getAllMetaTables, admin]);

  const displayedPrograms = (treatmentPlans || []).slice(limits.start - 1, limits.end);

  const groupUser: IUserReport = {
    Users: {
      Id: 0,
      Guid: "",
      CompanyId: admin.companyId,
      FullName: group.groupName,
      RoleId: 1,
      Account: null,
      Address: "",
      CityId: null,
      City: null,
      Birth: "",
      DateRegister: "",
      Email: null,
      FakeCompanyId: null,
      FirstName: group.groupName,
      GenderId: null,
      LastName: "",
      Passport: "",
      Phone: "",
      PlanId: 0,
      StatusId: 1,
      TherapistSpecializations: [],
    },
    AvailabilityHours: null,
    AppointmentsProgramsNum: null,
    TotalReports: 0,
    Recommends: null,
    RoleName: null,
  };

  const refreshPrograms = () => {
    refetch();
  };

  if (showAddOrEditProgramForm) {
    return (
      <AddTreatmentPlan
        onClose={() => {
          setShowAddOrEditProgramForm(false);
          setProgramToEdit(undefined);
        }}
        user={groupUser}
        program={programToEdit}
        treatmentGroupId={group.id}
      />
    );
  }

  return (
    <section className={`section ${styles.section}`}>
      <BackArrowButton
        text={t("PREVIOUS_SCREEN")}
        onClick={onClose}
        position={"center"}
      />
      
      <h1 className="title title_blue">
        {t('TREATMENT_PLANS')} - {group.groupName}
      </h1>

      <div className="header">
        <button
          type="button"
          className="addBtn"
          onClick={() => {
            setProgramToEdit(undefined);
            setShowAddOrEditProgramForm(true);
          }}
        >
          {t('ADD_TREATMENT_PLAN')}
          <span className="plus">+</span>
        </button>
      </div>

      {isLoading ? (
        <Loader />
      ) : error ? (
        <p className="text">{t('SOMETHING_WENT_WRONG_TRY_AGAIN_LATER')}</p>
      ) : !treatmentPlans?.length ? (
        <p className="text">{t('NO_TREATMENT_PLANS_FOR_GROUP')}</p>
      ) : (
        <>
          <div className={`list ${styles.list}`}>
            <div className={`listRow listRow_header ${styles.plan}`}>
              <p className="listCell">{t('NUMBER_1')}</p>
              <p className="listCell">{t('NAME_2')}</p>
              <p className="listCell">{t('NUMBER_OF_MEETINGS')}</p>
              <p className="listCell">{t("SESSION_DURATION")}</p>
              <p className="listCell">{t("COLUMN_FREQUENCY")}</p>
              <p className="listCell">{t("SUPPORT_ROLE")}</p>
              <p className="listCell">{t("COLUMN_STATUS")}</p>
              <p className="listCell">{t('CREATED_BY_1')}</p>
              <p className="listCell">{t("THERAPIST_NAME")}</p>
              <p className="listCell">{t('DATE_OF_CREATION')}</p>
              <p className="listCell">{t("COLUMN_OPTIONS")}</p>
            </div>
            {displayedPrograms.map((p, i) => {
              const index = limits.start - 1 + i;
              return (
                <div
                  key={p.appointmentsProgram?.id}
                  className={`listRow ${styles.plan} ${
                    index % 2 === 0 ? "colored" : ""
                  }`}
                >
                  <p className="listCell">{p.appointmentsProgram?.id}</p>
                  <p className="listCell">{p.appointmentsProgram.name}</p>
                  <p className="listCell">{p.appointmentsProgram.countOfAppointments}</p>
                  <p className="listCell">
                    {appointmentsProgramsData.AppointmentsDurations.find(
                      (a) => a.Id === p.appointmentsProgram.appointmentsDurationsId
                    )?.Minutes || "-"}
                  </p>
                  <p className="listCell">
                    {appointmentsProgramsData.AppointmentsSequence.find(
                      (a) => a.Id === p.appointmentsProgram.appointmentsSequenceId
                    )?.label || "-"}
                  </p>
                  <p className="listCell">
                    {appointmentsProgramsData.Roles.find(
                      (r) => r.Id === p.appointmentsProgram.roleId
                    )?.label || p.appointmentsProgram.roleId}
                  </p>
                  <p className="listCell">
                    <span
                      className={`${
                        p.appointmentsProgram.statusId === 0 ||
                        p.appointmentsProgram.statusId === 8
                          ? "status_inactive"
                          : ""
                      }`}
                    >
                      {appointmentsProgramsData.AppointmentsProgramsStatus.find(
                        (s) => s.Id === p.appointmentsProgram.statusId
                      )?.label || p.appointmentsProgram.statusId}
                    </span>
                  </p>
                  <p className="listCell">{p.createUserName || t(noDataLabel)}</p>
                  <p className="listCell">{p.therapistUserName || t(noDataLabel)}</p>
                  <p className="listCell">
                    {p.appointmentsProgram.createDate
                      ? getPrintDate(new Date(p.appointmentsProgram.createDate))
                      : "-"}
                  </p>
                  <div className="listCell">
                    <ActionButtonTooltip
                      className="btn btn_type_edit"
                      onClick={() => {
                        setProgramToEdit(p);
                        setShowAddOrEditProgramForm(true);
                      }}
                      text={t('EDIT_PLAN')}
                    />
                    <ActionButtonTooltip
                      className="btn btn_type_schedule"
                      onClick={() => {
                        setProgramToEdit(p);
                        setShowScheduleModal(true);
                      }}
                      text={t("VIEW_PLAN_DATES")}
                      tooltipPosition="left"
                    />
                    {p?.appointmentsProgram?.statusId === 0 ? (
                      <RestoreProgramButton
                        program={p.appointmentsProgram}
                        openModalSuccess={() => setOpenModalSuccess(true)}
                        openModalError={() => setOpenModalError(true)}
                        refreshPrograms={refreshPrograms}
                      />
                    ) : (
                      <ActionButtonTooltip
                        className={`btn btn_type_delete ${
                          p?.appointmentsProgram?.statusId !== 1
                            ? "btn_type_delete_disabled"
                            : ""
                        }`}
                        onClick={() => {
                          if (p?.appointmentsProgram?.statusId === 1) {
                            setProgramToDelete(p);
                            setOpenModal(true);
                          }
                        }}
                        text={
                          p?.appointmentsProgram?.statusId !== 1
                            ? t("PLAN_CONTAINS_SESSIONS_MESSAGE")
                            : t("CANCEL_PLAN")
                        }
                        tooltipPosition="left"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {treatmentPlans && treatmentPlans.length > 7 && (
            <ListPagination
              limits={limits}
              total={treatmentPlans.length}
              step={7}
              setLimits={setLimits}
            />
          )}
        </>
      )}

      {openModal && programToDelete && (
        <Modal
          onClose={() => {
            setOpenModal(false);
            setProgramToDelete(undefined);
          }}
        >
          <DeleteConfirmation
            text={t('ARE_YOU_SURE_TO_DELETE_PROGRAM', { name: programToDelete?.appointmentsProgram?.name })}
            onDelete={() => {
              const { usersId, therapistsId, ...filteredAppointmentsProgram } =
                programToDelete.appointmentsProgram;
              deleteProgram({
                ...filteredAppointmentsProgram,
                updateUsersGuid: admin.guid,
                updateDate: formatDateTime(new Date()),
              })
                .then((res) => {
                  if (res && "data" in res) {
                    setOpenModalSuccess(true);
                    refreshPrograms();
                  }
                })
                .catch((err) => {
                  setOpenModalError(true);
                  console.log(err);
                });
            }}
            onClose={() => {
              setOpenModal(false);
              setProgramToDelete(undefined);
            }}
          />
        </Modal>
      )}
      {openModalSuccess && (
        <Modal onClose={() => setOpenModalSuccess(false)}>
          <GeneralSuccessPopup onClose={() => setOpenModalSuccess(false)} />
        </Modal>
      )}
      {openModalError && (
        <Modal onClose={() => setOpenModalError(false)}>
          <ErrorPopup onClose={() => setOpenModalError(false)} />
        </Modal>
      )}
      {showScheduleModal && programToEdit && (
        <Modal
          wide={true}
          onClose={() => {
            setShowScheduleModal(false);
            setProgramToEdit(undefined);
          }}
        >
          <AppointmentsProgramSchedulePopup program={programToEdit} />
        </Modal>
      )}
    </section>
  );
}

export default GroupTreatmentPlans;
